import { useEffect, useState } from 'react';
import { useGameStore } from '../store/gameStore';
import { useAuth } from '../hooks/useAuth';
import { getGuestName } from '../lib/guestName';
import { getPlayerId } from '../lib/playerId';
import { supabase, invokeFunction } from '../supabase';
import {
  computeAtlas, computeArchetype,
  type AnswerEvent, type Atlas, type AtlasArchetype,
} from '../lib/cognitiveEngine';

import { RankCard, RatingGraph } from '../components/AtlasRank';
import { tierOf } from '../lib/atlasRank';
import { loadAtlasEvents } from '../lib/atlasData';
import { renderAtlasShareCard } from '../lib/shareCard';
import { shareCard } from '../lib/shareFlow';
import { SharePreview } from '../components/SharePreview';
import { BadgeShelf } from '../components/BadgeShelf';
import { ReminderToggle } from '../components/ReminderOffer';
import { reminderSupported } from '../lib/dailyReminder';

// The web address, like the score share: it plays in the browser first.
const ATLAS_SHARE_URL = 'https://wot.aimtech.ai/?utm_source=share&utm_medium=atlas';

interface PlayerStats {
  totalMatches: number;
  totalCorrect: number;
  totalQuestions: number;
  totalScore: number;
  bestScore: number;
}

interface RecentMatch {
  id: string;
  score: number;
  correct: number;
  total: number;
  realm: string;
  date: string;
  answers: { is_correct: boolean }[];
}

// The map's regions and the setup screen's arenas divide the same universe,
// so a weak region can point at the arena that feeds it most. Not a perfect
// overlap everywhere (Humanities spans two arenas); the button names its
// destination so the promise stays honest.
const REGION_ARENA: Record<string, { arena: string; name: string }> = {
  SCIENCES: { arena: 'FRONTIER', name: 'FRONTIER' },
  HUMANITIES: { arena: 'AFTER_DARK', name: 'AFTER DARK' },
  CULTURE: { arena: 'PRIME_TIME', name: 'PRIME TIME' },
  LIFESTYLE: { arena: 'GRAND_TOUR', name: 'GRAND TOUR' },
  ABSTRACT: { arena: 'AFTER_DARK', name: 'AFTER DARK' },
};

export function Profile() {
  const nickname = useGameStore((s) => s.nickname);
  const setView = useGameStore((s) => s.setView);
  const setGameMode = useGameStore((s) => s.setGameMode);
  const setSelectedRealm = useGameStore((s) => s.setSelectedRealm);
  const { user, avatarUrl, displayName, signOut } = useAuth();

  const [stats, setStats] = useState<PlayerStats>({
    totalMatches: 0, totalCorrect: 0, totalQuestions: 0,
    totalScore: 0, bestScore: 0
  });
  const [recentMatches, setRecentMatches] = useState<RecentMatch[]>([]);
  const [expandedMatchId, setExpandedMatchId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Written by the store when a survival run ends; read-only here.
  const survivalBest = (() => {
    try { return Number(localStorage.getItem('wot_survival_best') || '0'); }
    catch { return 0; }
  })();

  // Account deletion (Google Play requirement)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmCountdown, setConfirmCountdown] = useState(0);

  // We use the same nickname logic as Home page
  const displayNick = nickname ? nickname.toUpperCase() : (displayName ? displayName.split(' ')[0].toUpperCase() : getGuestName().toUpperCase());

  useEffect(() => {
    loadPlayerStats();
  }, []);

  // ── Cognitive Atlas (accumulated across games) ──
  const [atlas, setAtlas] = useState<Atlas | null>(null);
  const [atlasEvents, setAtlasEvents] = useState<AnswerEvent[]>([]);
  const [archetype, setArchetype] = useState<AtlasArchetype | null>(null);
  useEffect(() => {
    let active = true;
    (async () => {
      const events = await loadAtlasEvents(user ? displayNick : null);
      if (!active) return;
      // Zero answers is a state the rank card is designed for (UNRANKED, five
      // locked badges, a placement goal), so the atlas is always computed.
      const a = computeAtlas(events);
      setAtlas(a);
      setAtlasEvents(events);
      if (events.length > 0) setArchetype(computeArchetype(a, events));
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, displayNick]);

  const [atlasShared, setAtlasShared] = useState(false);
  const [atlasPreview, setAtlasPreview] = useState<{ url: string; text: string } | null>(null);
  async function handleShareAtlas() {
    if (!atlas) return;
    // Wordle's lesson: the share is a sentence, small and braggable.
    const rankLine = atlas.globalElo !== null
      ? `${tierOf(atlas.globalElo).name} · ${atlas.globalElo} on the Atlas ladder`
      : `${atlas.totalAnswers} answers toward placement`;
    const text = [
      `My Atlas Rank on World of Trivia`,
      rankLine + (archetype ? ` · ${archetype.name}` : ''),
      'Where do you place?',
    ].join('\n');
    let blob: Blob | null = null;
    try {
      blob = await renderAtlasShareCard({ nickname: displayNick, atlas, archetype });
    } catch { /* no card: the text still goes */ }
    // Web: the card on screen with copy and save, the same as the score
    // share. The desktop share sheet took the image and lost the text.
    const done = await shareCard({
      card: 'atlas', blob, fileName: 'wot-atlas.png', text, url: ATLAS_SHARE_URL,
      dialogTitle: 'Share your Atlas', web: 'preview',
    });
    if (done.outcome === 'copied') {
      setAtlasShared(true);
      setTimeout(() => setAtlasShared(false), 2000);
    }
    if (done.previewUrl) setAtlasPreview({ url: done.previewUrl, text: `${text}\n${ATLAS_SHARE_URL}` });
  }

  // 3-second cool-off before the destructive confirm becomes clickable
  useEffect(() => {
    if (!showDeleteConfirm) return;
    setConfirmCountdown(3);
    const t = setInterval(() => {
      setConfirmCountdown(c => {
        if (c <= 1) { clearInterval(t); return 0; }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [showDeleteConfirm]);

  async function handleDeleteAccount() {
    setDeleting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('You are not signed in.');
      await invokeFunction('delete-account', { access_token: session.access_token });
      await signOut();
      window.location.href = '/';
    } catch (e: any) {
      alert('Could not delete account: ' + (e?.message || 'unknown error'));
      setDeleting(false);
    }
  }

  async function loadPlayerStats() {
    try {
      // By player, not by name. Matching on nickname meant two guests who
      // picked the same name read one another's history, and renaming
      // yourself abandoned your own. users now holds one row per player.
      const playerId = await getPlayerId();
      let q = supabase
        .from('match_players')
        .select(`
          id, total_score, correct_count,
          matches!inner ( id, total_rounds, is_solo, started_at, created_at, status, mode ),
          users!inner ( nickname ),
          answers ( is_correct )
        `);
      // A device that has not played yet has no player row; fall back to the
      // old name match so returning testers still see their past.
      q = playerId ? q.eq('user_id', playerId) : q.ilike('users.nickname', displayNick);
      const { data: players, error } = await q;

      if (error || !players) {
        console.warn('Could not load stats:', error);
        setLoading(false);
        return;
      }

      // Sort by creation desc
      players.sort((a: any, b: any) => {
        const d1 = new Date(b.matches?.started_at || b.matches?.created_at || 0).getTime();
        const d2 = new Date(a.matches?.started_at || a.matches?.created_at || 0).getTime();
        return d1 - d2;
      });

      let totalCorrect = 0;
      let totalQuestions = 0;
      let totalScore = 0;
      let bestScore = 0;
      const recent: RecentMatch[] = [];

      for (const mp of players) {
        const match = (mp as any).matches;
        // A room that never found its second player is not a match: opened,
        // waited, left. It has a seat row but nothing was played.
        if (match?.status === 'waiting' || match?.status === 'abandoned') continue;
        const rounds = match?.total_rounds || 5;
        const correct = mp.correct_count || 0;
        const score = mp.total_score || 0;
        const answersList = (mp as any).answers || [];

        // Accuracy counts what was ANSWERED, never total_rounds: a survival
        // match writes its whole 95-question deck as total_rounds, and a run
        // that died at 6 was silently charged for the other 89. That is how
        // a profile with a climbing rating printed 9 per cent. Matches whose
        // answers were purged (the 2026-08-12 cleanup) are left out of the
        // ratio entirely rather than guessed at.
        if (answersList.length > 0) {
          totalCorrect += correct;
          totalQuestions += answersList.length;
        }
        totalScore += score;
        if (score > bestScore) bestScore = score;

        recent.push({
          id: match?.id,
          score,
          correct,
          total: answersList.length > 0 ? answersList.length : rounds,
          realm: match?.is_solo ? (match?.mode ?? 'SOLO') : `1 VS 1${match?.mode ? ` ${match.mode}` : ''}`,
          date: match?.started_at || match?.created_at || new Date().toISOString(),
          answers: answersList
        });
      }

      setStats({
        totalMatches: players.length,
        totalCorrect,
        totalQuestions,
        totalScore,
        bestScore
      });
      setRecentMatches(recent.slice(0, 20)); // Keep UI compact, max 20 matches
    } catch (err) {
      console.error('Stats load error:', err);
    }
    setLoading(false);
  }

  const correctRate = stats.totalQuestions > 0
    ? Math.round((stats.totalCorrect / stats.totalQuestions) * 100)
    : 0;

  return (
    <div className="page-profile-compact">
      {atlasPreview && (
        <SharePreview url={atlasPreview.url} text={atlasPreview.text} fileName="wot-atlas.png" onClose={() => setAtlasPreview(null)} />
      )}
      {/* Compact header row: back icon + title (same language as setup) */}
      <div style={{ display: 'flex', alignItems: 'center', height: 40, width: '100%', maxWidth: 400, margin: '4px auto 4px', padding: '0 16px' }}>
        <button
          onClick={() => setView('home')}
          aria-label="Back"
          style={{
            width: 48, height: 48, flexShrink: 0,
            background: '#20304E', border: '1px solid rgba(255,255,255,0.22)', borderRadius: 12,
            color: '#e8e8ea', fontSize: '1.35rem', cursor: 'pointer', lineHeight: 1,
          }}
        >
          &#8592;
        </button>
        <h1 style={{
          flex: 1, textAlign: 'center', margin: 0,
          fontFamily: 'var(--font-heading)', fontStyle: 'italic', fontWeight: 900,
          fontSize: '1.25rem', letterSpacing: '-0.02em', color: '#fff',
        }}>
          PROFILE
        </h1>
        <div style={{ width: 40, flexShrink: 0 }} />
      </div>

      {/* HEADER SECTION */}
      <div className="compact-header">
        <div className="compact-avatar-wrapper">
          {avatarUrl ? (
            <img src={avatarUrl} alt="" className="compact-avatar" referrerPolicy="no-referrer" />
          ) : (
            <div className="compact-avatar-placeholder">{displayNick[0]}</div>
          )}
        </div>
        <div className="compact-info">
          <h2 className="compact-name">{displayNick}</h2>
          <span className="compact-total-pts">{stats.totalScore.toLocaleString('en-US')} PTS</span>
        </div>
      </div>

      {/* STATS ROW */}
      <div className="compact-stats-row">
        <div className="compact-stat">
          <span className="c-stat-val text-teal">{stats.totalMatches}</span>
          <span className="c-stat-lbl">MATCHES</span>
        </div>
        <div className="compact-stat">
          <span className="c-stat-val text-white">{correctRate}%</span>
          <span className="c-stat-lbl">ACCURACY</span>
        </div>
        <div className="compact-stat">
          <span className="c-stat-val text-violet">{stats.bestScore > 999 ? `${(stats.bestScore / 1000).toFixed(1)}K` : stats.bestScore}</span>
          <span className="c-stat-lbl">BEST</span>
        </div>
        {/* Survival's own trophy: depth, not points. Local like the Atlas;
            hidden until a first run exists so the row never shows a zero. */}
        {survivalBest > 0 && (
          <div className="compact-stat">
            <span className="c-stat-val" style={{ color: '#F43F5E' }}>{survivalBest}</span>
            <span className="c-stat-lbl">DEEPEST RUN</span>
          </div>
        )}
      </div>

      {/* BADGES: earned from this player's answers on the server
          (20261012_badges.sql); takes no room until the server answers. */}
      <BadgeShelf nickname={displayNick} />

      {/* COGNITIVE ATLAS — the rank, the ladder, the climb. Rendered even at
          zero answers: UNRANKED with five locked badges and a placement goal
          is a designed state, not an error. */}
      {atlas && (
        <div className="compact-history-container">
          {/* The archetype is the headline and the map is its evidence. The
              previous order buried the only sentence a player actually reads
              underneath the chart. */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, marginBottom: 2 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem', letterSpacing: '0.28em', color: '#bebec6' }}>
              {archetype?.leaning ? 'LEANING' : 'COGNITIVE ATLAS'}
            </span>
            {archetype && (
              <>
                <span style={{
                  fontFamily: 'var(--font-heading)', fontStyle: 'italic', fontWeight: 900,
                  fontSize: '1.55rem', letterSpacing: '-0.01em', color: '#fff', lineHeight: 1.1,
                }}>
                  {archetype.name}
                </span>
                <span style={{ fontSize: '0.85rem', color: '#b6b6be', textAlign: 'center' }}>
                  {archetype.line}
                </span>
              </>
            )}
          </div>
          {/* The rank card carries everything the bars and the stone used to:
              tier + number per region on the badges, progress toward the next
              rung, and the placement goal when nothing is charted yet. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center', marginTop: 8 }}>
            <RankCard atlas={atlas} />
            {/* The map's darkest corner, made pressable. Only rendered when a
                region has actually been played and fallen behind; a fresh map
                has nothing to point at yet. */}
            {(() => {
              const played = atlas.regions.filter(r => r.n > 0);
              if (played.length < 2) return null;
              const weak = [...played].sort((a, b) => a.elo - b.elo)[0];
              const target = REGION_ARENA[weak.region];
              if (!target || weak.elo >= 1000) return null;
              return (
                <button
                  onClick={() => {
                    setGameMode('solo');
                    setSelectedRealm(target.arena);
                    setView('config');
                  }}
                  style={{
                    width: '100%', minHeight: 56, borderRadius: 12,
                    background: 'linear-gradient(120deg, rgba(62,123,250,0.22) 0%, transparent 65%), #1C2A45',
                    border: '2px solid rgba(62,123,250,0.55)',
                    boxShadow: '0 0 14px rgba(62,123,250,0.25)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    gap: 10, padding: '8px 14px', cursor: 'pointer',
                  }}
                >
                  <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
                    <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#A8A8BC' }}>Weakest region</span>
                    <span style={{ fontSize: '1rem', fontWeight: 800, color: '#fff' }}>{weak.name}</span>
                  </span>
                  <span style={{
                    fontFamily: 'var(--font-heading)', fontWeight: 800, fontSize: '0.85rem',
                    letterSpacing: '0.08em', color: '#0A1222', background: '#7EA8FF',
                    padding: '9px 14px', borderRadius: 999, flexShrink: 0,
                  }}>
                    TRAIN IN {target.name}
                  </span>
                </button>
              );
            })()}
            {atlasEvents.length >= 20 && <RatingGraph events={atlasEvents} />}
          </div>
          {/* Behavioral reads. These used to be three unlabeled stubs on an
              inner ring: visible but unreadable. As rows they actually say
              something, and an unobserved one is simply absent. */}
          {(atlas.pace || atlas.steadiness || atlas.resilience) && (
            <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
              {atlas.pace && (
                <div style={{ flex: 1, background: '#182541', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 12, padding: '10px 6px', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#e6e6e6' }}>{atlas.pace.band}</div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem', letterSpacing: '0.12em', color: '#c6c6ce', marginTop: 2 }}>PACE</div>
                </div>
              )}
              {atlas.steadiness && (
                <div style={{ flex: 1, background: '#182541', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 12, padding: '10px 6px', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#e6e6e6' }}>{atlas.steadiness.band}</div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem', letterSpacing: '0.12em', color: '#c6c6ce', marginTop: 2 }}>STEADINESS</div>
                </div>
              )}
              {atlas.resilience && (
                <div style={{ flex: 1, background: '#182541', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 12, padding: '10px 6px', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#e6e6e6' }}>
                    {Math.round(atlas.resilience.rate * 100)}%
                  </div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem', letterSpacing: '0.12em', color: '#c6c6ce', marginTop: 2 }}>RECOVERY</div>
                </div>
              )}
            </div>
          )}

          <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#8E8EA2', display: 'block', marginTop: 10, textAlign: 'center' }}>
            {atlas.totalAnswers} answers mapped
          </span>
          <button
            onClick={handleShareAtlas}
            style={{
              width: '100%', height: 50, marginTop: 8,
              background: '#16223A', border: `1px solid ${atlasShared ? '#14B8A6' : 'rgba(255,255,255,0.18)'}`, borderRadius: 10,
              color: atlasShared ? '#14B8A6' : '#dcdce0', fontFamily: 'var(--font-heading)', fontWeight: 800,
              fontSize: '0.82rem', letterSpacing: '0.18em', cursor: 'pointer', transition: 'all 0.15s ease',
            }}
          >
            {atlasShared ? 'COPIED' : 'SHARE ATLAS'}
          </button>
        </div>
      )}

      {/* MATCH HISTORY (DETAILED LIST) */}
      <div className="compact-history-container">
        <h3 className="section-label">MATCH HISTORY ({recentMatches.length})</h3>
        <div className="compact-history-list" style={{ maxHeight: '300px', overflowY: 'auto' }}>
          {loading ? (
            <div className="empty-msg">Syncing data...</div>
          ) : recentMatches.length > 0 ? (
            recentMatches.map((m, i) => {
              const expanded = expandedMatchId === m.id;
              return (
                <div key={i} className={`compact-match-wrapper ${expanded ? 'expanded' : ''}`} onClick={() => setExpandedMatchId(expanded ? null : m.id)} style={{ cursor: 'pointer' }}>
                  <div className="compact-match-row">
                    <span className="cm-realm">{m.realm}</span>
                    <span className="cm-score">{m.score.toLocaleString('en-US')} PTS</span>
                    <span className="cm-date" style={{ whiteSpace: 'nowrap' }}>
                      {new Date(m.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      {' '}
                      {new Date(m.date).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
                    </span>
                  </div>
                  {expanded && (
                    <div className="compact-match-details">
                      <div className="cm-dots">
                        {m.answers.length > 0 ? m.answers.map((a, idx) => (
                           <span key={idx} className={`cm-dot ${a.is_correct ? 'correct' : 'wrong'}`}></span>
                        )) : (
                           <span className="cm-no-data">Left early, no answers recorded.</span>
                        )}
                      </div>
                      <div className="cm-summary">
                         {m.correct} CORRECT / {m.total} QUESTIONS
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          ) : (
             <div className="empty-msg">No matches played yet.</div>
          )}
        </div>
      </div>

      {/* FOOTER ACTIONS */}
      <div className="compact-actions">
        {/* The Daily reminder's switch; Android only, like the reminder. */}
        {reminderSupported() && <ReminderToggle />}

        {(user?.email === 'kemal@aimtech.ai' || (import.meta.env.DEV && displayNick === 'KEMAL')) && (
          <button className="compact-btn-logout" onClick={() => { window.location.href = '/sys/wotgate'; }} style={{ background: '#3E4E70', borderColor: '#444', color: '#fff', marginBottom: '12px' }}>
             INTELLIGENCE OS
          </button>
        )}

        {/* Danger zone — account deletion (only for signed-in accounts) */}
        {user && !showDeleteConfirm && (
          <button
            onClick={() => setShowDeleteConfirm(true)}
            style={{
              background: 'transparent', border: '1px solid #F43F5E44', color: '#F43F5E',
              borderRadius: 8, minHeight: 44, padding: '10px 16px', width: '100%',
              fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: '0.85rem',
              letterSpacing: '0.08em', cursor: 'pointer',
            }}
          >
            DELETE ACCOUNT
          </button>
        )}

        {user && showDeleteConfirm && (
          <div style={{ border: '1px solid #F43F5E55', borderRadius: 10, padding: 16, background: '#150a0d' }}>
            <p style={{ color: '#f7b7c1', fontSize: '0.8rem', lineHeight: 1.5, margin: '0 0 14px 0' }}>
              This permanently deletes your account and sign-in identity (email &amp; Google login).
              Your saved profile and match history under this account can no longer be recovered.
              This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                style={{
                  flex: 1, background: '#243450', border: '1px solid #3E4E70', color: '#d2d2d8',
                  borderRadius: 8, minHeight: 44, fontWeight: 700, fontSize: '0.85rem',
                  letterSpacing: '0.06em', cursor: deleting ? 'default' : 'pointer',
                }}
              >
                CANCEL
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleting || confirmCountdown > 0}
                style={{
                  flex: 1,
                  background: deleting || confirmCountdown > 0 ? '#3a1a20' : '#F43F5E',
                  border: 'none', color: deleting || confirmCountdown > 0 ? '#a86b76' : '#fff',
                  borderRadius: 8, minHeight: 44, fontWeight: 800, fontSize: '0.85rem',
                  letterSpacing: '0.06em', cursor: deleting || confirmCountdown > 0 ? 'default' : 'pointer',
                }}
              >
                {deleting ? 'DELETING…' : confirmCountdown > 0 ? `CONFIRM (${confirmCountdown})` : 'CONFIRM DELETE'}
              </button>
            </div>
          </div>
        )}
      </div>


    </div>
  );
}
